import { DateTime } from "luxon";
import currency from "currency.js";
import classNames from "classnames";
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon } from "@icons";
import GroupedPayments from "./grouped-payments";
import { Badge, Button } from "@common";
import type { Invitation } from '@app-types';

interface StatementProps {
  idInvoice: number;
  dueDate: string;
  total: number;
  totalTxFees: number;
  feeTotal: number;
  totalPaid: number;
  invitations: Invitation[];
  hasFile: boolean;
  isExpired: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onDownload: (idInvoice: number) => void;
  statusEnum: {
    refunded: number;
    voided: number;
  };
}

const Statement: React.FC<StatementProps> = ({
  idInvoice,
  dueDate,
  total: invoiceTotal,
  totalTxFees,
  feeTotal: POTFee,
  totalPaid,
  invitations,
  hasFile,
  isExpired,
  isOpen,
  onToggle,
  onDownload,
  statusEnum
}) => {
  const txFee = currency(totalTxFees);
  const paid = currency(totalPaid);
  const invTotal = currency(invoiceTotal);
  const potFee = currency(POTFee);
  const grandTotal = invTotal.add(txFee).add(potFee);

  return (
    <div className={classNames(
      `bg-white border rounded-xl px-6 py-4 flex flex-col gap-4 transition-colors`,
      {
        "border-neutral-200 shadow-[0_1px_2px_0_rgba(34,41,51,0.24)]": isOpen,
        "border-neutral-100 hover:border-neutral-200": !isOpen
      }
    )}>
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full focus-visible:outline-focus-ring"
        aria-expanded={isOpen}
        aria-label={isOpen ? `Collapse statement` : `Expand statement`}
      >
        <div className="flex items-center gap-2">
          <h2 className="text-[20px] leading-6 font-medium text-neutral-800 tracking-[0.2px]">
            {DateTime.fromISO(dueDate).toFormat(`LLLL yyyy`)}
          </h2>
          {isExpired && (
            <Badge color="grey" size="sm" className="rounded-[4px]">
              Expired
            </Badge>
          )}
        </div>
        <div className="p-[6px] rounded-lg w-7 flex items-start shrink-0">
          {isOpen ? (
            <ChevronUpIcon className="w-4 h-4 text-neutral-900" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-neutral-900" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isOpen && (
        <>
          {/* Amount Due Section */}
          <dl className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-base leading-6 font-medium text-neutral-950 tracking-[0.16px]">
              <dt>Amount Due</dt>
              <dd className="text-right">{grandTotal.format()}</dd>
            </div>
            <div className="flex items-center justify-between text-sm leading-5 text-neutral-800 tracking-[0.14px]">
              <dt>Charges</dt>
              <dd className="text-right">{invTotal.format()}</dd>
            </div>
            {potFee.intValue !== 0 && (
              <div className="flex items-center justify-between text-sm leading-5 text-neutral-800 tracking-[0.14px]">
                <dt>Pay Over Time Fee</dt>
                <dd className="text-right">{potFee.format()}</dd>
              </div>
            )}
            <div className="flex items-center justify-between text-sm leading-5 text-neutral-800 tracking-[0.14px]">
              <dt>Credit Card / ACH Fees</dt>
              <dd className="text-right">{txFee.format()}</dd>
            </div>
          </dl>

          {/* Divider */}
          <div className="h-px bg-neutral-100" />

          {/* Paid Section */}
          <dl className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-base leading-6 font-medium text-neutral-950 tracking-[0.16px]">
              <dt>Paid</dt>
              <dd className="text-right">{paid.add(txFee).format()}</dd>
            </div>
            <GroupedPayments invitations={invitations} statusEnum={statusEnum} />
          </dl>

          {/* Divider */}
          <div className="h-px bg-neutral-100" />

          {/* Download Statement Button */}
          {hasFile && (
            <Button
              variant="primary"
              size="md"
              className="w-fit"
              startIcon={<DownloadIcon />}
              onClick={() => onDownload(idInvoice)}
            >
              Download Statement
            </Button>
          )}

          {/* Disclaimer */}
          <p className="text-xs leading-4 text-neutral-800 tracking-[0.12px]">
            Your Sunbound balance may differ from this statement due to updates to your bill, fees, or external payments.
          </p>
        </>
      )}
    </div>
  );
};

export default Statement;
